import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { containerVariants, itemVariants } from '@/components/animations';
import { workflowTiles } from '../constants/workflowTiles';
import { WorkflowDetails } from './WorkflowDetails';
import { SiteHeader } from '@/components/site-header';
import { cn } from '@/lib/utils';

interface ProjectDashboardProps {
  // subRoute might be "", "/flows", "/runs", "/connections"
  subRoute: string;
  onRouteChange: (route: string) => void;
}

// Memoize the tile list component
const TileList = React.memo(({ onRouteChange }: { onRouteChange: (route: string) => void }) => (
  <>
    <SiteHeader 
      title="Workflows"
      subtitle="Select a section to get started with your workflow automation"
    />
    
    <div className="flex-1 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="px-4 py-6 md:px-8 md:py-8"
      >
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {workflowTiles.map((tile) => (
            <motion.div
              key={tile.route}
              variants={itemVariants}
            >
              <Card
                onClick={() => onRouteChange(tile.route)}
                className={cn(
                  "group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer",
                  tile.isNew && "shadow-[0_0_25px_0px_hsl(var(--primary)/0.3)]"
                )}
              >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* New Badge */}
                {tile.isNew && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                      New
                    </span>
                  </div>
                )}

                {/* Main Content */}
                <div className="relative p-5 flex flex-col space-y-3">
                  {/* Icon Container */}
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                    {tile.icon}
                  </div>

                  {/* Title and Description */}
                  <div className="space-y-1">
                    <h3 className="text-base font-semibold tracking-tight">
                      {tile.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {tile.description}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  </>
));

export const ProjectDashboard = React.memo(({ subRoute, onRouteChange }: ProjectDashboardProps) => {
  if (!subRoute) {
    return <TileList onRouteChange={onRouteChange} />;
  }

  // Check if this is an Activepieces embed route, MCP route, Templates route, or Run Search route that needs full-screen flex layout
  const isActivepiecesEmbedRoute = subRoute.match(/^\/(flows|runs|connections|tables|todos)/);
  const isMcpRoute = subRoute === '/mcp' || subRoute.startsWith('/mcp/');
  const isTemplatesRoute = subRoute === '/templates' || subRoute.startsWith('/templates/');
  const isRunSearchRoute = subRoute === '/runs-search' || subRoute.startsWith('/runs-search');

  // Render WorkflowDetails with flex layout for Activepieces, MCP, Templates, and Run Search routes
  return (
    <div className={(isActivepiecesEmbedRoute || isMcpRoute || isTemplatesRoute || isRunSearchRoute) ? "h-full w-full flex flex-col" : ""}>
      <WorkflowDetails subRoute={subRoute} />
    </div>
  );
});